import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type {
  UpdateChildIndicatorInput,
  UpdateIndicatorMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateIndicatorDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type UpdateIndicatorTRPCInput = Omit<
  UpdateChildIndicatorInput,
  'schedule' | 'Title' | 'Type'
> &
  ScheduleTRPCInput & {
    Title: string;
    Type: NonNullable<UpdateChildIndicatorInput['Type']>;
  };

export const useUpdateIndicator = createMutationHook({
  name: 'updateIndicator',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.indicator.update.mutationOptions({ onSuccess }),
  mapInput: ({
    schedule,
    Title,
    Type,
    ...rest
  }: UpdateChildIndicatorInput): UpdateIndicatorTRPCInput => {
    if (!Title || !Type) {
      throw new Error('Title and Type are required for indicator update');
    }

    return {
      ...rest,
      Title,
      Type,
      ...mapScheduleToTRPC(schedule),
    };
  },
  invalidate: (trpc) => [
    trpc.frontend.indicator.register.queryKey(),
    trpc.frontend.indicator.indicatorById.queryKey(),
    trpc.frontend.indicator.indicatorsByParentId.queryKey(),
    trpc.frontend.indicator.indicatorResultsByIndicatorId.queryKey(),
  ],
  mapResponse: (result: { Id: string }): UpdateIndicatorMutation => ({
    updateChildIndicator: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: UpdateIndicatorDocument,
  graphqlVariables: (input: UpdateChildIndicatorInput) => ({ object: input }),
  evictFields: ['indicator'],
});
